/**
 * Build Your Stack — the RESUPPLY CALCULATOR (SITE_PAGES_SPEC.md ADDENDUM 13).
 *
 * ⚠ THIS REPLACES THE DOSE PRESCRIBER. Addendum 10 ratified the old mechanic
 * "unchanged, visuals only"; addendum 13 supersedes it and changes the mechanic.
 *
 * What it does now: three questions on one screen — how many a day, where you
 * need it, how often — and it answers "how much do we ship you, and how often."
 * It does not prescribe a dose and it does not ask anyone to self-classify.
 *
 * ⚠ THE INVERSION, which is the whole point (a13 ③):
 *   OLD — buildStack() ran an exhaustive SKU-combination search scored
 *         `fit * 2.5 + cost * 0.6 + lines.length * 1.0` over intervals
 *         {4,6,8,10,12,16}, then BACK-DERIVED a cadence from whichever
 *         combination won. The cadence was tied to a scoring function no user
 *         could see, which is exactly what Mick could not follow:
 *         "how often we ship — I have no idea what we're trying to tie it to."
 *   NEW — the interval is an INPUT the user picks and quantity is the OUTPUT.
 *         need = perDay × weeks × 7, filled greedily per a13 ④. The entire
 *         scoring function is gone; nothing here is unexplainable to a visitor.
 *
 * ⚠ THE INTERVALS ARE NOT A DESIGN CHOICE. Every interval offered must
 * correspond to a live, purchasable Recharge selling plan (a13 ⑦ #2) — today
 * 4/8/12 weeks on the jar. They come from the section's `intervals_weeks`
 * setting so an alignment fix is an editor change, not a deploy. Never display
 * a cadence the cart cannot honour.
 *
 * Deleted here and not coming back (a13 ⑥): the exhaustive search and its
 * score, INTERVALS = [4,6,8,10,12,16], coffeeReferenceMg, CUP_STEPS,
 * cupFraction(), and the per-dose `label`/`compare` strings. `blurb` survives
 * as hint text only. Removing the coffee readout closes register #13 off the
 * live surface.
 *
 * ⚠ STANDING HAZARD (a13 ⑥, unchanged by this rebuild): the section keeps its
 * OWN COPY of catalog facts in a JSON payload that nothing cross-checks. It has
 * drifted twice — a dead 3/6/12/20-pouch ladder, and a 20%-vs-25% discount no
 * customer would ever have been charged. Re-read the payload by hand every time
 * the catalog moves, until it is wired to live product data.
 */
(() => {
  'use strict';

  const round2 = (n) => Math.round(n * 100) / 100;
  const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const centsEach = (price, gummies) => `${Math.round((price / gummies) * 100)}¢ each`;

  /**
   * a13 ④ — the pouch half of the greedy fill.
   *
   * ⚠ THE CAP IS PRICE DOMINANCE, NOT THE NUMBER 9 — and getting that wrong is
   * a live defect, not a rounding preference. a13 ④ writes the cap as "9" and
   * justifies it with arithmetic: "ten singles cost $59.90 against $29.99 for
   * the same 100 gummies, so a fill that reaches ten is always wrong." The
   * justification is right and the number is too generous. Break-even is
   * $29.99 / $5.99 = 5.007, so SIX singles ($35.94, 60 gummies) is already
   * beaten outright by one 10-Pack ($29.99, 100 gummies) — cheaper AND more
   * product. Capping at a literal 9 let the widget recommend a strictly worse
   * order at 1-a-day/Pouches/8-weeks, which is the shape of defect the whole
   * per-gummy ladder exists to make obvious.
   *
   * So: promote whenever the singles cost at least as much as a pack. That
   * encodes the spec's own reasoning instead of its round number, keeps
   * a13 ⑦ #3 true (six is fewer than ten), and re-derives itself if either
   * price moves. The literal cap stays as a backstop in case a future price
   * change breaks dominance.
   *
   * Promote rather than truncate — truncating would silently under-ship the
   * supply the visitor asked for, which is a worse failure than one extra pack.
   */
  function fillPouches(need, pack, single, cap) {
    const lines = [];
    if (need <= 0) return lines;

    let packs = Math.floor(need / pack.gummies);
    const remainder = need - packs * pack.gummies;
    let singles = Math.ceil(remainder / single.gummies);

    if (singles > cap || singles * single.price >= pack.price) {
      packs += 1;
      singles = 0;
    }

    if (packs > 0) lines.push({ sku: pack, qty: packs });
    if (singles > 0) lines.push({ sku: single, qty: singles });
    return lines;
  }

  /**
   * The greedy fill (a13 ④). Replaces the exhaustive search outright.
   *
   *   need = perDay × weeks × 7
   *   Jar      →  jars = ceil(need / 200)
   *   Pouches  →  packs = floor(need / 100); singles = ceil(remainder / 10), cap 9
   *   Both     →  1 jar base; remainder filled with packs, then singles;
   *               additional whole jars only when the remainder >= 200
   */
  function buildStack(payload, perDay, formatId, weeks, subscribing) {
    const need = perDay * weeks * 7;
    const cap = payload.singlesCap;

    const jar = payload.skus.find((s) => s.format === 'jar');
    const pouches = payload.skus
      .filter((s) => s.format === 'pouch')
      .sort((a, b) => b.gummies - a.gummies);
    const pack = pouches[0];
    const single = pouches[pouches.length - 1];

    let lines;

    if (formatId === 'jar') {
      lines = [{ sku: jar, qty: Math.max(1, Math.ceil(need / jar.gummies)) }];
    } else if (formatId === 'pouch') {
      lines = fillPouches(need, pack, single, cap);
      if (lines.length === 0) lines = [{ sku: single, qty: 1 }];
    } else {
      let jars = 1;
      let remainder = need - jar.gummies;

      if (remainder >= jar.gummies) {
        const extra = Math.floor(remainder / jar.gummies);
        jars += extra;
        remainder -= extra * jar.gummies;
      }

      const pouchLines = fillPouches(remainder, pack, single, cap);

      /* "Both" promises a jar at base AND pouches on you (a13 ③). When the
         remainder lands at or below zero the fill would return a jar-only
         stack — which is the format the visitor did NOT pick. Guarantee one
         pouch so the answer matches the question that was asked. */
      lines = [{ sku: jar, qty: jars }].concat(
        pouchLines.length ? pouchLines : [{ sku: single, qty: 1 }]
      );
    }

    /* ⚠ A SUBSCRIPTION MUST NEVER BE SINGLES-ONLY (a12 ③). The $5.99 pouch is
       "underwater on fulfillment as a standalone order" — subscribed at 20% that
       is $4.79 revenue against $0.82 COGS and a parcel that costs more than the
       $3.97 left over, repeating forever. It is reachable: Pouches / 1 a day /
       every 4 weeks composes 3 singles and no pack.

       Fixed in the FILL, not by withholding the single's selling plan — the plan
       is what makes a jar + packs + singles order possible, which Mick asked for
       explicitly. A one-time order of singles alone is fine and stays available;
       only the recurring case rounds up to a whole pack. Singles keep exactly the
       role a12 ③ gives them: a top-up INSIDE a larger order. */
    if (subscribing && lines.length && lines.every((l) => l.sku === single)) {
      lines = [{ sku: pack, qty: 1 }];
    }

    const subPrice = (price) => round2(price * (1 - payload.subDiscount));
    const gummiesSupplied = lines.reduce((a, l) => a + l.sku.gummies * l.qty, 0);
    const oneTimeTotal = round2(lines.reduce((a, l) => a + l.sku.price * l.qty, 0));
    const subTotal = round2(lines.reduce((a, l) => a + subPrice(l.sku.price) * l.qty, 0));

    return {
      lines,
      weeks,
      gummiesSupplied,
      daysCovered: Math.floor(gummiesSupplied / perDay),
      oneTimeTotal,
      subTotal,
      savings: round2(oneTimeTotal - subTotal),
      monthlyEquivalent: round2(subTotal / (weeks / 4.345)),
    };
  }

  function init(root) {
    const catalogEl = root.querySelector('[data-cfg-catalog]');
    let catalog = {};
    try {
      catalog = catalogEl ? JSON.parse(catalogEl.textContent) : {};
    } catch (e) {
      catalog = {};
    }

    const payloadEl = root.querySelector('[data-cfg-payload]');
    if (!payloadEl) return;
    let payload;
    try {
      payload = JSON.parse(payloadEl.textContent);
    } catch {
      return;
    }
    if (!payload.intervals || payload.intervals.length === 0) return;

    const q = (sel) => root.querySelector(sel);

    let perDay = payload.perDayDefault;
    let format = 'mix';
    let weeks = payload.intervals.includes(8) ? 8 : payload.intervals[0];
    /* ⭐ a15 ④ — ONE-TIME IS THE DEFAULT, and this line is the whole of it.
       It read 'sub', so every visitor arrived already in Subscribe mode with
       nine live selling plans behind it: the exact inverse of a15 ①, "a
       customer cannot become a subscriber by accident." The matching
       aria-pressed pair in configurator.liquid is flipped to match, and the
       markup order there puts One time first for the same reason.

       ⚠ a15 ③'s two-step reveal is already structural here and needs no
       control: a13 ③ made the ship interval question 3, a user-picked INPUT,
       so nobody reaches this toggle without having named a cadence. What was
       missing is the commit label, below. */
    let plan = 'once';

    const formatsEl = q('[data-cfg-formats]');
    formatsEl.innerHTML = payload.formats
      .map(
        (f) => `
        <button type="button" class="cfg__format" role="radio" aria-checked="${f.id === format}" tabindex="${f.id === format ? 0 : -1}" data-format="${f.id}">
          <span class="cfg__format-name">${f.name}</span>
          <span class="cfg__format-tag">${f.tagline}</span>
        </button>`
      )
      .join('');

    /* a13 ③ — the intervals are rendered from the payload, which is rendered
       from the section setting, which must mirror the live selling plans. No
       interval exists in this file. */
    const intervalsEl = q('[data-cfg-intervals]');
    intervalsEl.innerHTML = payload.intervals
      .map(
        (w) => `
        <button type="button" class="cfg__interval" role="radio" aria-checked="${w === weeks}" tabindex="${w === weeks ? 0 : -1}" data-weeks="${w}">
          <span class="cfg__interval-n numeral">${w}</span>
          <span class="cfg__interval-unit">weeks</span>
        </button>`
      )
      .join('');

    const range = q('[data-cfg-range]');
    range.min = String(payload.perDayMin);
    range.max = String(payload.perDayMax);
    range.value = String(perDay);

    q('[data-cfg-ticks]').innerHTML = [payload.perDayMin, payload.perDayMax]
      .map((n) => `<span>${n}</span>`)
      .join('');

    /* a13 ③ / a14 ⑤ — hint text is OUTPUT. The visitor picks a number and the
       tool tells them what it is for; they never shop a list for a version of
       themselves. Anchors are sparse, so take the nearest anchor at or below
       the chosen number. */
    const hintFor = (n) =>
      payload.hints.reduce((best, h) => (h.n <= n && h.n >= best.n ? h : best), payload.hints[0]);

    /* ⭐ a24 §④ B6 — THE RADIOGROUPS KEEP THE PROMISE THEIR ARIA MAKES.
       Both groups were built as role="radio" buttons inside role="radiogroup",
       which is correct markup, and then behaved like nothing of the kind:
       three tab stops per group, no arrow handling. A screen reader announced
       "radio, 1 of 3", the user pressed an arrow, and nothing moved — the
       standard reading of which is that the control is broken.

       ⚠ Stated honestly: this was never a keyboard trap and axe never flagged
       it. Tab and Enter always worked. It is a broken promise, not a lockout,
       and the fix is the ARIA Authoring Practices radiogroup pattern.

       Exactly one item per group is tabbable, and arrows move focus AND
       selection together — for radios the two are the same act.

       ⚠ FAIL-SAFE, and the reason this is a function rather than a ternary:
       if nothing is checked, the group would have ZERO tab stops and drop out
       of the tab order entirely — strictly worse than the three-stop version
       this replaces. Fall back to the first item so the group is always
       reachable. */
    const syncRoving = (items) => {
      const tabbable = items.find((b) => b.getAttribute('aria-checked') === 'true') || items[0];
      items.forEach((b) => {
        b.tabIndex = b === tabbable ? 0 : -1;
      });
    };

    function render() {
      const stack = buildStack(payload, perDay, format, weeks, plan === 'sub');
      const hint = hintFor(perDay);
      const jar = payload.skus.find((s) => s.format === 'jar');

      root.querySelectorAll('[data-cfg-gummy]').forEach((g) => {
        const n = Number(g.getAttribute('data-cfg-gummy'));
        if (n <= perDay) g.removeAttribute('data-off');
        else g.setAttribute('data-off', '');
      });

      q('[data-cfg-mg]').textContent = String(perDay * payload.mgPerGummy);
      q('[data-cfg-count]').textContent = String(perDay);

      /* The supply-side translation that replaces the persona label: a plain
         statement of what the number means for resupply. */
      const jarWeeks = Math.round(jar.gummies / perDay / 7);
      q('[data-cfg-jar-life]').textContent =
        jarWeeks <= 1 ? 'a jar lasts about a week' : `a jar lasts about ${jarWeeks} weeks`;

      q('[data-cfg-situation]').textContent = hint.situation;
      q('[data-cfg-hint]').textContent = hint.blurb;

      /* a13 ⑤ — the swap, in the currency Punch'd actually wins. Both sides
         read from the SAME per-gummy constants as the mg readout above
         (a13 ⑦ #6): one source, not two. */
      q('[data-cfg-swap-mine]').textContent =
        `${perDay * payload.calPerGummy} cal · ${perDay * payload.sugarGramsPerGummy} g sugar`;
      q('[data-cfg-swap-mine-label]').textContent = `Your ${perDay} a day`;

      const formatItems = Array.from(formatsEl.querySelectorAll('.cfg__format'));
      formatItems.forEach((b) => {
        b.setAttribute('aria-checked', String(b.getAttribute('data-format') === format));
      });
      syncRoving(formatItems);

      q('[data-cfg-format-detail]').textContent = payload.formats.find(
        (f) => f.id === format
      ).detail;

      const intervalItems = Array.from(intervalsEl.querySelectorAll('.cfg__interval'));
      intervalItems.forEach((b) => {
        b.setAttribute('aria-checked', String(Number(b.getAttribute('data-weeks')) === weeks));
      });
      syncRoving(intervalItems);

      /* Per-gummy is shown PER LINE, not blended across the stack. That is what
         keeps a13 ⑦ #5 true: changing the interval changes quantity, never the
         per-gummy price of a rung — and it is what makes the 60¢/30¢/20¢ ladder
         teach itself (a12 ②). */
      q('[data-cfg-lines]').innerHTML = stack.lines
        .map(
          (l) => `
          <li class="cfg__line">
            <span class="cfg__line-left">
              <span class="cfg__line-qty">${l.qty}&times;</span>
              <span>
                <span class="cfg__line-name">${l.sku.name}</span><br>
                <span class="cfg__line-meta">${l.sku.gummies * l.qty} gummies &middot; ${centsEach(l.sku.price, l.sku.gummies)}${l.sku.dtcExclusive ? ' &middot; online only' : ''}</span>
              </span>
            </span>
            <span class="cfg__line-price">${money(l.sku.price * l.qty)}</span>
          </li>`
        )
        .join('');

      const subOn = plan === 'sub';
      q('[data-cfg-cadence]').textContent = subOn
        ? `Ships every ${weeks} weeks`
        : `A ${weeks}-week supply, shipped once`;
      q('[data-cfg-coverage]').textContent = `covers ${stack.daysCovered} days`;

      /* ⚠ NOT IN a13 — added in build, because the fill rule as specified can
         promise a cadence it makes no sense to keep. The floor of an order is
         one whole jar, so at 1 a day "The Jar, every 4 weeks" ships 200 days of
         gummies every 28 days. The fill is correct and the interval is a real
         selling plan; what is wrong is quietly pairing the two. Nothing here
         overrides the visitor's choice — it states the arithmetic already on
         screen and names the two ways out. ⛔ The real fix is a design call for
         the spec session: whether an interval should be OFFERED when the
         minimum order overshoots it this far. */
      const gap = weeks * 7;
      const overshoot = q('[data-cfg-overshoot]');
      if (stack.daysCovered >= gap * 2) {
        overshoot.textContent = `Heads up: the smallest order in this format covers ${stack.daysCovered} days, well past the ${weeks}-week gap. A longer interval, or pouches instead of a jar, fits closer.`;
        overshoot.hidden = false;
      } else {
        overshoot.textContent = '';
        overshoot.hidden = true;
      }

      root.querySelectorAll('[data-cfg-plan]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-cfg-plan') === plan));
      });

      const total = subOn ? stack.subTotal : stack.oneTimeTotal;
      q('[data-cfg-price]').textContent = money(total);
      q('[data-cfg-strike]').textContent = subOn ? money(stack.oneTimeTotal) : '';
      q('[data-cfg-price-note]').textContent = subOn
        ? `every ${weeks} weeks · about ${money(stack.monthlyEquivalent)} a month · you save ${money(stack.savings)} each time`
        : 'one-time order · ships once, nothing recurring';
      q('[data-cfg-per-gummy]').textContent = `$${(total / stack.gummiesSupplied).toFixed(3)}`;
      /* ⭐ a15 ⑦ #3 — THE COMMIT BUTTON NAMES THE RECURRENCE. It read "Start
         my stack", which is a nice line that tells you nothing about being
         charged again in `weeks` weeks. The interval is already on screen
         twice, but not on the thing you press, and a15 ③ is explicit that the
         button is where it has to be. One-time takes "Buy now" per a15 ⑦ #5 —
         never "one-time purchase" (Mick).

         ⚠ Assigning textContent also drops the padlock span the template
         renders inside this button; that was already true before a15 and the
         padlock belongs to the disabled pre-JS state. */
      q('[data-cfg-cta]').textContent = subOn ? `Subscribe: ships every ${weeks} weeks` : 'Buy now';

      /* ⚠ THE BUTTON'S ENABLED STATE IS DERIVED, NEVER ASSUMED. It shipped
         hardcoded `disabled` in Wave 1 and stayed that way long after the
         catalog landed — it looked like a live green button for days. Now it
         is disabled only when a line in the CURRENT stack genuinely cannot be
         bought: no variant id, or out of stock, or (subscribing) no selling
         plan for the chosen interval. */
      const blocked = stack.lines.filter((l) => !buyableLine(l, subOn, weeks));
      const cta = q('[data-cfg-cta]');
      cta.disabled = blocked.length > 0;
      cta.title = blocked.length ? 'Some items in this stack are unavailable right now.' : '';

      range.style.setProperty(
        '--fill',
        `${((perDay - payload.perDayMin) / (payload.perDayMax - payload.perDayMin)) * 100}%`
      );
    }

    range.addEventListener('input', (e) => {
      perDay = Number(e.target.value);
      render();
    });
    formatsEl.addEventListener('click', (e) => {
      const b = e.target.closest('[data-format]');
      if (!b) return;
      format = b.getAttribute('data-format');
      render();
    });
    intervalsEl.addEventListener('click', (e) => {
      const b = e.target.closest('[data-weeks]');
      if (!b) return;
      weeks = Number(b.getAttribute('data-weeks'));
      render();
    });

    /* a24 §④ B6, the other half of syncRoving above. Both orientations are
       wired because the two groups do not lay out the same way and a user
       cannot see which axis a radiogroup thinks it has: Right/Down advance,
       Left/Up retreat, Home/End jump to the ends. Selection wraps, which is
       what the APG specifies for radios and what makes a 3-item group usable
       without looking.

       ⚠ `commit` re-renders BEFORE focus moves, so the new tabindex is in
       place when the browser sets focus — otherwise focus would land on an
       element the roving pass is about to mark tabindex="-1". */
    const rovingKeys = (groupEl, itemSelector, commit) => {
      groupEl.addEventListener('keydown', (e) => {
        const current = e.target.closest(itemSelector);
        if (!current) return;
        const items = Array.from(groupEl.querySelectorAll(itemSelector));
        const i = items.indexOf(current);
        if (i === -1 || items.length === 0) return;

        let next;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = items[(i + 1) % items.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = items[(i - 1 + items.length) % items.length];
        else if (e.key === 'Home') next = items[0];
        else if (e.key === 'End') next = items[items.length - 1];
        else return;

        e.preventDefault();
        commit(next);
        next.focus();
      });
    };

    rovingKeys(formatsEl, '[data-format]', (b) => {
      format = b.getAttribute('data-format');
      render();
    });
    rovingKeys(intervalsEl, '[data-weeks]', (b) => {
      weeks = Number(b.getAttribute('data-weeks'));
      render();
    });
    root.querySelectorAll('[data-cfg-plan]').forEach((b) =>
      b.addEventListener('click', () => {
        plan = b.getAttribute('data-cfg-plan');
        render();
      })
    );

    /* ── the cart bridge (added 2026-07-31) ────────────────────────────
       ⭐ a15 IS SATISFIED WITHOUT A NEW CONTROL HERE. The interval is
       already a deliberate INPUT (a13 ③), the mode defaults to one-time
       (a15 ④), and the commit button NAMES the recurrence before it can be
       pressed — "Subscribe: ships every 8 weeks". So this only had to carry
       out what the button already promises.

       ⚠ `selling_plan` is attached ONLY in subscribe mode, and only from the
       plan id the catalog emitted for the chosen interval. There is no path
       that attaches one in one-time mode. */
    function skuEntry(line) {
      const h = line.sku && line.sku.handle;
      return h ? catalog[h] : null;
    }

    function buyableLine(line, subscribing, weeksChosen) {
      const e = skuEntry(line);
      if (!e || !e.variantId || !e.available) return false;
      if (subscribing && !(e.plans && e.plans[String(weeksChosen)])) return false;
      return true;
    }

    async function addStackToCart() {
      const subscribing = plan === 'sub';
      const stack = buildStack(payload, perDay, format, weeks, subscribing);
      const items = [];
      for (const line of stack.lines) {
        const e = skuEntry(line);
        if (!buyableLine(line, subscribing, weeks)) return false;
        const item = { id: e.variantId, quantity: line.qty };
        if (subscribing) item.selling_plan = e.plans[String(weeks)];
        items.push(item);
      }
      if (!items.length) return false;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) return false;
      window.location.href = '/cart';
      return true;
    }

    q('[data-cfg-cta]').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      btn.disabled = true;
      const ok = await addStackToCart();
      if (!ok) {
        btn.disabled = false;
        btn.textContent = 'Could not add that stack. Try again.';
        window.setTimeout(render, 2500);
      }
    });

    render();
  }

  const boot = () => document.querySelectorAll('[data-configurator]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
